import type { QueryClient } from "@tanstack/react-query";
import { QUERY_CACHE_FLAG } from "@/lib/launchdarkly/flags";
import { peekQueryClient } from "@/queries/client";
import { scopeFor, ScopeNotReadyError, type QueryScope } from "@/queries/scope";

/**
 * Cache-invalidation hints pushed over the workspace's notification stream
 * (NG-2666), so another user's write reaches this tab in seconds rather than
 * when an entry's freshness runs out.
 *
 * A hint names a domain and a scope, never data: `{type: "cache.invalidate",
 * workspace_id, domain, scope}`, plus any field the service adds (it adds
 * `channel`), which is ignored. This tab marks the matching cached reads stale
 * and re-reads them through the normal API, so permissions stay on the read
 * path. The expiry times stay as the backstop for any writer that sends no
 * hint.
 *
 * Only with `query-cache` on: with it off nothing is cached under a key, so
 * there is nothing to invalidate.
 */
const CACHE_INVALIDATE_EVENT = "cache.invalidate";

interface TCacheHint {
  readonly workspace_id: string;
  readonly domain: string;
  readonly scope: string;
}

/**
 * What each hint does, keyed `domain:scope`: one entry per covered read, and a
 * hint for anything not listed is ignored. A read held only in Query
 * invalidates its keys (`invalidateScoped`); a read a store projects into MobX
 * goes through that store, which also refreshes what is on screen.
 */
const HINT_TARGETS: Readonly<
  Record<string, (root: TCacheHintsHost, workspaceSlug: string, cache: TCached) => void>
> = {
  // R2, both `archived` lists, dropped as `ProjectStore.markBankWrite` drops
  // them; an open picker (Create Task, Add projects) is re-read.
  "projects:bank": (root, workspaceSlug) => root.project.markBankChangedElsewhere(workspaceSlug),
};

/**
 * The parts of the root store the hints use. Spelled out rather than
 * imported, because the root store registers the listener and an import back
 * would be a cycle.
 */
interface TCacheHintsHost {
  readonly notification: {
    addStreamListener(listener: {
      readonly event: string;
      onMessage(workspaceSlug: string, data: string): void;
      onReopen?(workspaceSlug: string): void;
    }): () => void;
  };
  readonly featureFlags: { isEnabled(key: string): boolean };
  readonly workspace: { findBySlug(slug: string): { readonly id: string } | undefined };
  readonly project: { markBankChangedElsewhere(slug: string): void };
}

/** Start reading hints off this tab's notification streams; returns the function that stops it. */
export function listenForCacheHints(root: TCacheHintsHost): () => void {
  return root.notification.addStreamListener({
    event: CACHE_INVALIDATE_EVENT,
    onMessage(workspaceSlug, data) {
      if (!root.featureFlags.isEnabled(QUERY_CACHE_FLAG)) return;
      const hint = parseHint(workspaceSlug, data);
      if (!hint) return;
      // The service already files streams by workspace; a hint for another
      // workspace is ignored here as well.
      if (hint.workspace_id !== root.workspace.findBySlug(workspaceSlug)?.id) return;
      const target = HINT_TARGETS[`${hint.domain}:${hint.scope}`];
      if (!target) return;
      const cached = cachedScope(workspaceSlug);
      if (!cached) return;
      target(root, workspaceSlug, cached);
    },
    onReopen(workspaceSlug) {
      if (!root.featureFlags.isEnabled(QUERY_CACHE_FLAG)) return;
      const cached = cachedScope(workspaceSlug);
      if (!cached) return;
      // Pub/sub keeps nothing for a dropped stream, so any hint sent while it
      // was down is lost: mark everything this principal holds in the
      // workspace stale, and the next read of each asks again.
      void cached.client.invalidateQueries({
        queryKey: [cached.scope.tenant, cached.scope.principal],
      });
    },
  });
}

function parseHint(workspaceSlug: string, data: string): TCacheHint | null {
  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch (error) {
    console.warn("[cache-hints] unreadable hint ignored", { workspaceSlug, data }, error);
    return null;
  }
  if (
    typeof payload !== "object" ||
    payload === null ||
    typeof (payload as TCacheHint).workspace_id !== "string" ||
    typeof (payload as TCacheHint).domain !== "string" ||
    typeof (payload as TCacheHint).scope !== "string"
  ) {
    console.warn("[cache-hints] malformed hint ignored", { workspaceSlug, data });
    return null;
  }
  return payload as TCacheHint;
}

/**
 * The client and scope to invalidate under, or null when there is nothing
 * cached to reach: no client yet, or no signed-in principal to key under.
 */
interface TCached {
  readonly client: QueryClient;
  readonly scope: QueryScope;
}

function cachedScope(workspaceSlug: string): TCached | null {
  const client = peekQueryClient();
  if (!client) return null;
  try {
    return { client, scope: scopeFor(workspaceSlug) };
  } catch (error) {
    if (!(error instanceof ScopeNotReadyError)) throw error;
    // Nothing is cached before the session resolves, so nothing is stale.
    console.warn("[cache-hints] hint before the session resolved; nothing to invalidate", {
      workspaceSlug,
    });
    return null;
  }
}
